import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/** Max length enforced on the slide text fields (mirrors the backend 422 rule). */
export const SLIDE_FIELD_MAX = 300;

const quietInput =
  "border-transparent bg-transparent px-2 py-1 shadow-none hover:border-border focus-visible:border-input focus-visible:ring-1 focus-visible:ring-offset-0";

interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  className?: string;
  "aria-label"?: string;
}

/** A quietly styled textarea that keeps the reading layout (border on focus). */
export function EditableText({ value, onChange, rows = 4, className, ...rest }: EditableTextProps) {
  return (
    <Textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className={cn(quietInput, "text-sm leading-7", className)}
      aria-label={rest["aria-label"]}
    />
  );
}

interface CountedTextareaProps extends EditableTextProps {
  max?: number;
}

/** EditableText with a live character counter; turns red when over the limit. */
export function CountedTextarea({ max = SLIDE_FIELD_MAX, ...props }: CountedTextareaProps) {
  const over = props.value.length > max;
  return (
    <div className="w-full">
      <EditableText {...props} />
      <p
        className={cn(
          "mt-1 text-right text-xs tabular-nums",
          over ? "font-medium text-red-600" : "text-muted",
        )}
      >
        {props.value.length}/{max}
      </p>
    </div>
  );
}

interface EditableListProps {
  items: string[];
  onChange: (items: string[]) => void;
  rows?: number;
  "aria-label"?: string;
}

/**
 * List editor as a newline-separated textarea: one line per item.
 * Empty lines are dropped when the parent reads the items back.
 */
export function EditableList({ items, onChange, rows, ...rest }: EditableListProps) {
  return (
    <EditableText
      value={items.join("\n")}
      onChange={(text) => onChange(text.split("\n"))}
      rows={rows ?? Math.max(3, items.length + 1)}
      className="leading-6"
      aria-label={rest["aria-label"]}
    />
  );
}

/** Drops empty lines from a list edited via EditableList. */
export function cleanList(items: string[]): string[] {
  return items.map((i) => i.trim()).filter((i) => i.length > 0);
}
